// Organization portal: what an organization's contacts (chief, HR, training officer) see. Roster status,
// cleared / referred once released, event dates, a status CSV, released report PDFs where NGF shares them,
// and a way to send in the next roster. NGF staff open it from an organization's page as a preview.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ErrorBox } from '../components/Layout.tsx';
import { get, isStaff, post } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useData } from '../lib/useData.ts';

type State = 'complete' | 'in_progress' | 'due';
interface Row {
  id: string; firstName: string; lastName: string; employeeId: string | null; lastTestedOn: string | null; releasedOn: string | null;
  state: State; paperReport: boolean | null; respiratorQualified: boolean | null; referred: boolean | null; packetId: string | null;
  clearanceLetterId?: string | null;   // the clearance update letter of a follow-up decision
}
interface OrgView {
  org: { id: string; name: string; portalEnabled: boolean; portalPackets: boolean };
  counts: { total: number; complete: number; inProgress: number; due: number; referred: number };
  events: { id: string; startsOn: string; endsOn: string | null; location: string | null; status: string }[];
  submissions: { id: string; submittedAt: string; filename: string | null; rowCount: number; note: string | null; status: string; submittedBy: string; eventOn: string | null }[];
}

const STATE_LABEL: Record<State, string> = { complete: 'Complete', in_progress: 'In progress', due: 'Due' };
const FILTERS: [string, string][] = [['', 'Everyone'], ['due', 'Due'], ['in_progress', 'In progress'], ['complete', 'Complete'], ['referred', 'Referred']];
const PAGE = 200;
const day = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

function outcome(r: Row) {
  if (r.state !== 'complete' && !r.releasedOn) return <span className="muted">—</span>;
  if (r.paperReport) return <span className="muted small">See paper report</span>;
  if (r.referred) return <span className="badge provisional">Referred</span>;
  if (r.respiratorQualified === true) return <span className="badge signed">Cleared</span>;
  if (r.respiratorQualified === false) return <span className="badge provisional">Not cleared</span>;
  return <span className="muted">—</span>;
}

/** /portal: a contact with one organization goes straight to it; with several, picks one. */
export function PortalHome() {
  const { data, error } = useData<{ id: string; name: string }[]>('/api/portal/orgs');
  if (error) return <ErrorBox error={error} />;
  if (!data) return <p className="muted">Loading…</p>;
  if (data.length === 1) return <Navigate to={`/portal/${data[0]!.id}`} replace />;
  return (
    <div className="card" style={{ maxWidth: 620 }}>
      <h1>Organization portal</h1>
      {!data.length ? <p>Your account is not linked to an organization with an active portal yet. Please contact North Greenville Fitness.</p> : (
        <><p>Choose an organization:</p>
          <ul className="portal-orgs">{data.map((o) => <li key={o.id}><Link to={`/portal/${o.id}`}>{o.name}</Link></li>)}</ul></>
      )}
    </div>
  );
}

export function Portal() {
  const { id } = useParams();
  const { me } = useAuth();
  const preview = !!me && isStaff(me.role);
  const view = useData<OrgView>(`/api/portal/orgs/${id}`);
  const orgs = useData<{ id: string; name: string }[]>(preview ? null : '/api/portal/orgs');
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [text, setText] = useState('');
  const [state, setState] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const load = useCallback(async (offset: number) => {
    setLoading(true); setErr(null);
    try {
      const page = await get<Row[]>(`/api/portal/orgs/${id}/roster?limit=${PAGE}&offset=${offset}${q ? `&q=${encodeURIComponent(q)}` : ''}${state ? `&state=${state}` : ''}`);
      setRows((prev) => (offset ? [...prev, ...page] : page));
      setMore(page.length === PAGE);
    } catch (x) { setErr(x); } finally { setLoading(false); }
  }, [id, q, state]);
  useEffect(() => { void load(0); }, [load]);

  if (view.error) return <ErrorBox error={view.error} />;
  if (!view.data) return <p className="muted">Loading…</p>;
  const { org, counts, events } = view.data;
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => (e.endsOn ?? e.startsOn) >= today).reverse();
  const recent = events.filter((e) => (e.endsOn ?? e.startsOn) < today).slice(0, 4);
  const tile = (label: string, n: number, filter: string, tone = '') => (
    <button type="button" className={`portal-tile ${tone} ${state === filter ? 'on' : ''}`} onClick={() => setState(state === filter ? '' : filter)}>
      <span className="n">{n.toLocaleString()}</span><span className="l">{label}</span>
    </button>
  );

  return (
    <>
      {preview && <div className="alert info">Preview: this is what {org.name}'s contacts see{org.portalEnabled ? '' : ' (their portal is currently switched off)'}.
        {' '}<Link to={`/organizations/${org.id}`}>Back to the organization</Link></div>}
      <div className="card portal-head">
        <div>
          <div className="muted small">Organization portal</div>
          <h1 style={{ margin: 0 }}>{org.name}</h1>
        </div>
        {orgs.data && orgs.data.length > 1 && (
          <select aria-label="Switch organization" value={org.id} onChange={(e) => nav(`/portal/${e.target.value}`)} style={{ maxWidth: 280 }}>
            {orgs.data.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
      </div>

      <div className="portal-tiles">
        {tile('On roster', counts.total, '')}
        {tile('Complete (last 12 months)', counts.complete, 'complete', 'ok')}
        {tile('In progress', counts.inProgress, 'in_progress')}
        {tile('Due', counts.due, 'due', 'warn')}
        {tile('Referred', counts.referred, 'referred', 'warn')}
      </div>

      <div className="grid2 portal-grid">
        <div className="card">
          <div className="spread">
            <h2 style={{ margin: 0 }}>Roster</h2>
            <a className="btn secondary" href={`/api/portal/orgs/${org.id}/roster.csv`}>Download status (CSV)</a>
          </div>
          <form className="row" style={{ marginTop: '0.75rem' }} onSubmit={(e) => { e.preventDefault(); setQ(text.trim()); }}>
            <div><input type="search" aria-label="Find by name or employee ID" placeholder="Find by name or employee ID" value={text} onChange={(e) => setText(e.target.value)} /></div>
            <div style={{ flex: '0 0 auto' }}>
              <select aria-label="Show" value={state} onChange={(e) => setState(e.target.value)}>
                {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <div style={{ flex: '0 0 auto' }}><button className="secondary">Search</button></div>
          </form>
          <ErrorBox error={err} />
          {!rows.length ? <p className="muted">{loading ? 'Loading…' : 'No one matches.'}</p> : (
            <div className="table-scroll">
              <table style={{ marginTop: '0.5rem' }}>
                <thead><tr><th>Name</th><th>ID #</th><th>Last tested</th><th>Status</th><th>Outcome</th>{org.portalPackets && <th>Report</th>}</tr></thead>
                <tbody>{rows.map((r) => (
                  <tr key={r.id}>
                    <td><strong>{r.lastName}, {r.firstName}</strong></td>
                    <td>{r.employeeId ?? '—'}</td>
                    <td>{r.lastTestedOn ? day(r.lastTestedOn) : <span className="muted">Never</span>}</td>
                    <td><span className={`badge portal-${r.state}`}>{STATE_LABEL[r.state]}</span></td>
                    <td>{outcome(r)}</td>
                    {org.portalPackets && <td>{r.packetId ? <a href={`/api/portal/packets/${r.packetId}/pdf`}>PDF</a> : <span className="muted">—</span>}
                      {r.clearanceLetterId && <div><a href={`/api/portal/letters/${r.clearanceLetterId}/file`}>Clearance letter</a></div>}</td>}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {more && <button className="secondary" style={{ marginTop: '0.75rem' }} disabled={loading} onClick={() => void load(rows.length)}>{loading ? 'Loading…' : `Show ${PAGE} more`}</button>}
          <p className="muted small" style={{ marginTop: '0.75rem' }}>
            <strong>Complete</strong>: tested and released by North Greenville Fitness in the last 12 months. <strong>In progress</strong>: tested, waiting for physician review.
            {' '}<strong>Due</strong>: nothing completed in the last 12 months. Test results are not shown here{org.portalPackets ? '; released reports can be downloaded' : ''}.
          </p>
        </div>

        <div>
          <div className="card">
            <h2>Testing dates</h2>
            {!upcoming.length && !recent.length ? <p className="muted">No testing scheduled yet.</p> : <>
              {upcoming.length > 0 && <><h3 className="small">Coming up</h3><ul className="portal-events">
                {upcoming.map((e) => <li key={e.id}><strong>{day(e.startsOn)}{e.endsOn && e.endsOn !== e.startsOn ? ` – ${day(e.endsOn)}` : ''}</strong>{e.location ? <div className="muted small">{e.location}</div> : null}</li>)}</ul></>}
              {recent.length > 0 && <><h3 className="small">Recent</h3><ul className="portal-events">
                {recent.map((e) => <li key={e.id}>{day(e.startsOn)}{e.location ? <span className="muted small"> · {e.location}</span> : null}</li>)}</ul></>}
            </>}
          </div>
          <RosterUpload orgId={org.id} disabled={preview} submissions={view.data.submissions} onSent={() => void view.reload()} />
        </div>
      </div>
    </>
  );
}

function RosterUpload({ orgId, disabled, submissions, onSent }: { orgId: string; disabled: boolean; submissions: OrgView['submissions']; onSent: () => void }) {
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await post<{ rows: number }>(`/api/portal/orgs/${orgId}/rosters`, { csv: file.text, filename: file.name, note: note || undefined });
      setMsg(`Sent: ${r.rows} ${r.rows === 1 ? 'person' : 'people'}. North Greenville Fitness will add them before your testing date.`);
      setFile(null); setNote(''); onSent();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  const status: Record<string, string> = { pending: 'Waiting for NGF', imported: 'Added', dismissed: 'Not used' };
  return (
    <div className="card">
      <h2>Send your next roster</h2>
      <p className="small muted">A CSV file with a header row: last name, first name, and if you have them date of birth, sex and employee ID.{' '}
        <a href="/roster-sample.csv" download="NGF-roster-sample.csv">Download a sample to fill in</a> (Excel opens it; save as CSV).</p>
      <form onSubmit={send}>
        <div className="field"><input type="file" accept=".csv,text/csv" aria-label="Roster CSV file" disabled={disabled}
          onChange={async (e) => { const f = e.target.files?.[0]; setFile(f ? { name: f.name, text: await f.text() } : null); }} /></div>
        <div className="field"><input aria-label="Note for NGF (optional)" placeholder="Note for NGF (optional)" value={note} maxLength={1000} disabled={disabled} onChange={(e) => setNote(e.target.value)} /></div>
        <button disabled={disabled || busy || !file}>{busy ? 'Sending…' : 'Send roster'}</button>
        {disabled && <span className="muted small" style={{ marginLeft: 8 }}>(contacts only)</span>}
      </form>
      <ErrorBox error={err} />
      {msg && <div className="alert ok" style={{ marginTop: '0.75rem' }}>{msg}</div>}
      {submissions.length > 0 && (
        <ul className="portal-events" style={{ marginTop: '0.75rem' }}>{submissions.map((s) => (
          <li key={s.id} className="small">{new Date(s.submittedAt).toLocaleDateString()} · {s.rowCount} {s.rowCount === 1 ? 'person' : 'people'} · {s.submittedBy}
            {' · '}<span className={`badge ${s.status === 'imported' ? 'signed' : s.status === 'pending' ? 'draft' : ''}`}>{status[s.status]}</span>
            {s.eventOn ? <span className="muted"> for {day(s.eventOn)}</span> : null}</li>
        ))}</ul>
      )}
    </div>
  );
}
